import type React from "react";
import CommonFilter from "@/components/common/CommonFilter";
import CommonTable, { type TableColumn } from "@/components/common/CommonTable";
import SalaryConfigTable, {
  type ISalaryUserBackup,
} from "@/components/common/SalaryConfigTable";
import TablePagination from "@/components/common/TablePagination";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import SalaryPeriodSelector from "./SalaryPeriodSelector";
import type {
  IAttendanceSummaryUser,
  ISalaryPreviewError,
  ISalaryPreviewUser,
} from "@/interface/attendance.interface";
import type { IPageInfo } from "@/interface/common.interface";
import type { IUser } from "@/interface/employeeOnBoard";
import type { SalarySubTab } from "../../hooks/CreateSalaryHooks/createSalary.utils";
import { showToastNotification } from "@/utility/helper";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CalendarCheck,
  CheckCircle2,
  DollarSign,
  Layers,
  Lock,
} from "lucide-react";

import type { IBranchWiseSalaryHead } from "@/interface/salaryHead.interface";

interface CreateSalaryTabsProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  searchValue: string;
  onSearchChange: (query: string) => void;
  employeeToolbar: React.ReactNode;
  attendanceToolbar: React.ReactNode;
  salaryToolbar: React.ReactNode;
  employeeColumns?: TableColumn<IUser>[];
  attendanceColumns: TableColumn<IAttendanceSummaryUser>[];
  validUsersColumns: TableColumn<ISalaryPreviewUser>[];
  errorUsersColumns: TableColumn<ISalaryPreviewError>[];
  employees?: IUser[];
  salaryConfigData: ISalaryUserBackup[];
  branchesWiseSalaryHead?: IBranchWiseSalaryHead[];
  attendanceList: IAttendanceSummaryUser[];
  paginatedSalaryData: Array<ISalaryPreviewUser | ISalaryPreviewError>;
  salaryPreviewUsers: ISalaryPreviewUser[];
  salaryPreviewErrors: ISalaryPreviewError[];
  selectedEmployeeIds: Set<string | number>;
  isAttendanceLocked?: boolean;
  paginationData?: IPageInfo;
  salaryConfigPaginationData: IPageInfo;
  attendancePaginationData: IPageInfo;
  salaryPreviewPaginationData: IPageInfo;
  attendanceStartDate: string;
  attendanceEndDate: string;
  salarySubTab: SalarySubTab;
  isLoadingEmployees?: boolean;
  isLoadingSalaryConfig: boolean;
  isLoadingAttendance: boolean;
  isLoadingSalaryPreview: boolean;
  setPaginationData?: React.Dispatch<React.SetStateAction<IPageInfo>>;
  setSalaryConfigPaginationData: React.Dispatch<React.SetStateAction<IPageInfo>>;
  setAttendancePaginationData: React.Dispatch<React.SetStateAction<IPageInfo>>;
  setSalaryPreviewPaginationData: React.Dispatch<React.SetStateAction<IPageInfo>>;
  setSalarySubTab: React.Dispatch<React.SetStateAction<SalarySubTab>>;
  onAttendanceDateRangeChange: (start: string, end: string) => void;
  onChangePageSize?: (newSize: number) => void;
  onSalaryConfigChangePageSize: (newSize: number) => void;
  onAttendanceChangePageSize: (newSize: number) => void;
  onSalaryPreviewChangePageSize: (newSize: number) => void;
  onSelectAll: (checked: boolean) => void;
  onAttendanceSelectAll: (checked: boolean) => void;
  onSelectItem: (itemOrId: IUser | string, checked: boolean) => void;
  onSaveUserSalary?: (
    userId: string,
    userSalary: { branchSalaryHeadId: string; amount: number }[],
  ) => Promise<void>;
  onViewSalaryBreakup?: (user: ISalaryUserBackup) => void;
}

const CreateSalaryTabs = ({
  activeTab,
  onTabChange,
  searchValue,
  onSearchChange,
  employeeToolbar,
  attendanceToolbar,
  salaryToolbar,
  attendanceColumns,
  validUsersColumns,
  errorUsersColumns,
  salaryConfigData,
  branchesWiseSalaryHead = [],
  attendanceList,
  paginatedSalaryData,
  salaryPreviewUsers,
  salaryPreviewErrors,
  selectedEmployeeIds,
  isAttendanceLocked = false,
  salaryConfigPaginationData,
  attendancePaginationData,
  salaryPreviewPaginationData,
  attendanceStartDate,
  attendanceEndDate,
  salarySubTab,
  isLoadingSalaryConfig,
  isLoadingAttendance,
  isLoadingSalaryPreview,
  setSalaryConfigPaginationData,
  setAttendancePaginationData,
  setSalaryPreviewPaginationData,
  setSalarySubTab,
  onAttendanceDateRangeChange,
  onSalaryConfigChangePageSize,
  onAttendanceChangePageSize,
  onSalaryPreviewChangePageSize,
  onSelectAll,
  onAttendanceSelectAll,
  onSelectItem,
  onSaveUserSalary,
  onViewSalaryBreakup,
}: CreateSalaryTabsProps) => {
  const isAttendanceCurrentlyLocked =
    isAttendanceLocked ||
    attendanceList.some((item) => item.attendanceLock?.isLocked);

  const handleTabChangeWithValidation = (targetTab: string) => {
    if (targetTab === "attendance") {
      // Check if any selected employees do NOT have salary configured
      if (selectedEmployeeIds.size > 0 && salaryConfigData.length > 0) {
        const unConfiguredUsers = salaryConfigData.filter(
          (u) =>
            selectedEmployeeIds.has(String(u.id)) &&
            (!Array.isArray(u.salaries) || u.salaries.length === 0),
        );

        if (unConfiguredUsers.length > 0) {
          const count = unConfiguredUsers.length;
          const names = unConfiguredUsers
            .slice(0, 3)
            .map((u) => u.name)
            .filter(Boolean)
            .join(", ");
          const more = count > 3 ? ` and ${count - 3} more` : "";
          showToastNotification(
            "danger",
            `Cannot proceed to Step 2 (Attendance): Salary is not configured for ${count} selected employee${count > 1 ? "s" : ""}${names ? ` (${names}${more})` : ""}. Please configure their salary structure first or deselect them.`,
            { id: "salary-not-configured-selection" },
          );
          return;
        }
      }
    }

    if (targetTab === "salary") {
      // 1. Direct jump from Step 1 to Step 3 check
      if (activeTab === "employee") {
        showToastNotification(
          "warning",
          "Please proceed step-by-step: Go to Step 2 (Attendance) to verify and lock attendance first.",
          { id: "step-order-validation" },
        );
        return;
      }

      // 2. Check if any users are selected
      if (selectedEmployeeIds.size === 0) {
        showToastNotification(
          "warning",
          "Please select at least one employee before proceeding to Salary Preview.",
          { id: "no-users-selected" },
        );
        return;
      }

      // 3. Check attendance lock and salary created for selected users
      if (attendanceList.length > 0) {
        const selectedUsers = attendanceList.filter((item) =>
          selectedEmployeeIds.has(String(item.user?.id)),
        );

        if (selectedUsers.length > 0) {
          const allSalaryCreated = selectedUsers.every(
            (item) => item.salaryCreated?.isCreated,
          );
          if (allSalaryCreated) {
            showToastNotification(
              "warning",
              "Salary has already been created for all selected employee(s).",
              { id: "salary-already-created" },
            );
            return;
          }

          const unLockedUsers = selectedUsers.filter(
            (item) =>
              !item.attendanceLock?.isLocked && !item.salaryCreated?.isCreated,
          );
          if (unLockedUsers.length > 0) {
            const count = unLockedUsers.length;
            const names = unLockedUsers
              .slice(0, 3)
              .map((u) => u.user?.name)
              .filter(Boolean)
              .join(", ");
            const more = count > 3 ? ` and ${count - 3} more` : "";
            showToastNotification(
              "danger",
              `Cannot access Step 3: Attendance is not locked for ${count} selected employee${count > 1 ? "s" : ""}${names ? ` (${names}${more})` : ""}. Please lock attendance in Step 2 first.`,
              { id: "attendance-not-locked-selection" },
            );
            return;
          }
        }
      }
    }

    onTabChange(targetTab);
  };

  const handleProceedToStep3 = () => {
    handleTabChangeWithValidation("salary");
  };

  return (
    <Tabs value={activeTab} onValueChange={handleTabChangeWithValidation} className="w-full">
      <TabsList className="group-data-horizontal/tabs:h-11 md:group-data-horizontal/tabs:h-12 overflow-auto gap-1.5 p-1.5 bg-slate-100/90 border border-slate-200/60 rounded-xl mb-5 w-full items-center justify-start sm:justify-center">
        <MainTab
          value="employee"
          icon={<Layers className="w-4 h-4 text-primary" />}
          badge={
            selectedEmployeeIds.size > 0 ? (
              <span className="ml-1 text-[10px] font-bold bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                {selectedEmployeeIds.size}
              </span>
            ) : null
          }
        >
          1. Salary Break-Up & Employees
        </MainTab>
        <MainTab
          value="attendance"
          icon={<CalendarCheck className="w-4 h-4 text-emerald-600" />}
          badge={
            attendanceList.length > 0 ? (
              <span className="ml-1 text-[10px] font-bold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                {attendanceList.length}
              </span>
            ) : null
          }
        >
          2. Attendance (Pay Cycle 21st-20th)
        </MainTab>
        <MainTab
          value="salary"
          icon={<DollarSign className="w-4 h-4 text-blue-600" />}
          badge={
            salaryPreviewUsers.length > 0 ? (
              <span className="ml-1 text-[10px] font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                {salaryPreviewUsers.length} Ready
              </span>
            ) : null
          }
        >
          3. Salary Preview & Generation
        </MainTab>
      </TabsList>

      {/* ── Stage 1: Salary Break-Up (Heads Configuration) ──────── */}
      <TabsContent value="employee" className="space-y-4 outline-none">
        {/* Filter bar — Search & Salary Heads Excel Import/Export toolbar */}
        <CommonFilter
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          searchPlaceholder="Search employees by name, code..."
          additionalFilters={employeeToolbar}
          createSalaryFilter={true}
        />

        {/* Salary Config & Heads Break-Up Table */}
        <SalaryConfigTable
          data={salaryConfigData}
          salaryHead={branchesWiseSalaryHead}
          loading={isLoadingSalaryConfig}
          showCheckbox={true}
          selectedItems={selectedEmployeeIds}
          onSelectAll={onSelectAll}
          onSelectItem={(item, checked) => {
            if (item && item.id) {
              onSelectItem(String(item.id), Boolean(checked));
            }
          }}
          currentPage={salaryConfigPaginationData.currentPage}
          pageSize={salaryConfigPaginationData.pageSize}
          stickyColumns={7}
          onSaveUserSalary={onSaveUserSalary}
          onViewUserDetails={onViewSalaryBreakup}
          emptyMessage="No salary break-up configuration data available for selected branch/site"
        />
        <TablePagination
          paginationData={salaryConfigPaginationData}
          setPaginationData={setSalaryConfigPaginationData}
          handleChangePageSize={onSalaryConfigChangePageSize}
          isLoading={isLoadingSalaryConfig}
        />

        {/* Stage 1 Footer Navigation */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-slate-100 mt-5 bg-slate-50/50 p-3.5 rounded-xl">
          <p className="text-xs text-slate-500 font-medium">
            {selectedEmployeeIds.size > 0
              ? `✓ ${selectedEmployeeIds.size} employees selected. Verify salary heads and proceed to attendance.`
              : "Select employees above or click 'Select All Branch Users' to proceed."}
          </p>
          <Button
            type="button"
            onClick={() => handleTabChangeWithValidation("attendance")}
            className="gap-2 text-xs font-semibold h-9 px-4 rounded-lg bg-primary hover:bg-primary/90 text-white cursor-pointer w-full sm:w-auto"
          >
            <span>Proceed to Step 2: Attendance (21st-20th)</span>
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </TabsContent>

      {/* ── Stage 2: Attendance Verification & Locking ────────────── */}
      <TabsContent value="attendance" className="space-y-4 outline-none">
        <CommonFilter
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          searchPlaceholder="Search attendance records..."
          additionalFilters={
            <div className="flex items-center gap-3 flex-wrap">
              <SalaryPeriodSelector
                startDate={attendanceStartDate}
                endDate={attendanceEndDate}
                onDateRangeChange={onAttendanceDateRangeChange}
              />
              {attendanceToolbar}
            </div>
          }
        />

        <CommonTable
          columns={attendanceColumns}
          data={attendanceList}
          loading={isLoadingAttendance}
          showCheckbox
          stickyColumns={3}
          selectedItems={selectedEmployeeIds}
          onSelectAll={onAttendanceSelectAll}
          onSelectItem={(item, checked) => {
            if (item && typeof item === "object" && "user" in item) {
              onSelectItem(String(item.user.id), Boolean(checked));
            }
          }}
          getItemId={(item) => String(item.user.id)}
          isItemSelectable={(item) => !item.salaryCreated?.isCreated}
          emptyMessage="No attendance data available for selected branch, site, and category"
        />

        <TablePagination
          paginationData={attendancePaginationData}
          setPaginationData={setAttendancePaginationData}
          handleChangePageSize={onAttendanceChangePageSize}
          isLoading={isLoadingAttendance}
        />

        {/* Stage 2 Footer Navigation */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-slate-100 mt-5 bg-slate-50/50 p-3.5 rounded-xl">
          <Button
            type="button"
            variant="outline"
            onClick={() => onTabChange("employee")}
            className="gap-2 text-xs font-semibold h-9 px-4 rounded-lg cursor-pointer w-full sm:w-auto"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Step 1: Salary Heads</span>
          </Button>
          <Button
            type="button"
            onClick={handleProceedToStep3}
            className="gap-2 text-xs font-semibold h-9 px-4 rounded-lg bg-primary hover:bg-primary/90 text-white cursor-pointer w-full sm:w-auto"
          >
            <span>Proceed to Step 3: Salary Preview</span>
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </TabsContent>

      {/* ── Stage 3: Salary Preview, Adjustments & Generation ──────── */}
      <TabsContent value="salary" className="space-y-4 outline-none">
        {!isAttendanceCurrentlyLocked ? (
          <div className="bg-rose-50 border border-rose-200/80 rounded-2xl p-8 text-center space-y-4 my-4 max-w-lg mx-auto shadow-xs">
            <div className="w-14 h-14 bg-rose-100 rounded-full flex items-center justify-center mx-auto text-rose-600">
              <Lock className="w-7 h-7" />
            </div>
            <div className="space-y-1.5">
              <h3 className="text-base font-bold text-slate-900">Attendance Not Locked</h3>
              <p className="text-xs text-slate-600 leading-relaxed">
                Step 3 (Salary Preview) requires attendance to be locked for the selected pay cycle first.
              </p>
            </div>
            <Button
              type="button"
              onClick={() => onTabChange("attendance")}
              className="gap-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold h-9 px-5 rounded-xl cursor-pointer shadow-xs"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Go to Step 2 to Lock Attendance</span>
            </Button>
          </div>
        ) : (
          <>
            <CommonFilter
              searchValue={searchValue}
              onSearchChange={onSearchChange}
              searchPlaceholder="Search salary preview records..."
              additionalFilters={
                <div className="flex items-center gap-3 flex-wrap">
                  <SalaryPeriodSelector
                    startDate={attendanceStartDate}
                    endDate={attendanceEndDate}
                    onDateRangeChange={onAttendanceDateRangeChange}
                  />
                  {salaryToolbar}
                </div>
              }
            />

            <SalaryPreviewTabs
              salarySubTab={salarySubTab}
              setSalarySubTab={setSalarySubTab}
              salaryPreviewUsers={salaryPreviewUsers}
              salaryPreviewErrors={salaryPreviewErrors}
              paginatedSalaryData={paginatedSalaryData}
              validUsersColumns={validUsersColumns}
              errorUsersColumns={errorUsersColumns}
              isLoadingSalaryPreview={isLoadingSalaryPreview}
              salaryPreviewPaginationData={salaryPreviewPaginationData}
              setSalaryPreviewPaginationData={setSalaryPreviewPaginationData}
              onSalaryPreviewChangePageSize={onSalaryPreviewChangePageSize}
            />

            {/* Stage 3 Footer Navigation */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-slate-100 mt-5 bg-slate-50/50 p-3.5 rounded-xl">
              <Button
                type="button"
                variant="outline"
                onClick={() => onTabChange("attendance")}
                className="gap-2 text-xs font-semibold h-9 px-4 rounded-lg cursor-pointer w-full sm:w-auto"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to Step 2: Attendance</span>
              </Button>
              <p className="text-xs text-slate-500 font-medium text-center sm:text-right">
                {salaryPreviewUsers.length > 0
                  ? `Ready to generate payroll for ${salaryPreviewUsers.length} employees.`
                  : "Review calculations and resolve any configuration errors."}
              </p>
            </div>
          </>
        )}
      </TabsContent>
    </Tabs>
  );
};

const MainTab = ({
  value,
  icon,
  badge,
  children,
}: {
  value: string;
  icon: React.ReactNode;
  badge?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <TabsTrigger
    value={value}
    className="flex items-center justify-center gap-2 h-full px-4 py-2 font-semibold text-xs sm:text-sm text-slate-600 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-xs rounded-lg transition-all whitespace-nowrap cursor-pointer"
  >
    {icon}
    <span>{children}</span>
    {badge}
  </TabsTrigger>
);

interface SalaryPreviewTabsProps {
  salarySubTab: SalarySubTab;
  setSalarySubTab: React.Dispatch<React.SetStateAction<SalarySubTab>>;
  salaryPreviewUsers: ISalaryPreviewUser[];
  salaryPreviewErrors: ISalaryPreviewError[];
  paginatedSalaryData: Array<ISalaryPreviewUser | ISalaryPreviewError>;
  validUsersColumns: TableColumn<ISalaryPreviewUser>[];
  errorUsersColumns: TableColumn<ISalaryPreviewError>[];
  isLoadingSalaryPreview: boolean;
  salaryPreviewPaginationData: IPageInfo;
  setSalaryPreviewPaginationData: React.Dispatch<React.SetStateAction<IPageInfo>>;
  onSalaryPreviewChangePageSize: (newSize: number) => void;
}

const SalaryPreviewTabs = ({
  salarySubTab,
  setSalarySubTab,
  salaryPreviewUsers,
  salaryPreviewErrors,
  paginatedSalaryData,
  validUsersColumns,
  errorUsersColumns,
  isLoadingSalaryPreview,
  salaryPreviewPaginationData,
  setSalaryPreviewPaginationData,
  onSalaryPreviewChangePageSize,
}: SalaryPreviewTabsProps) => {
  const hasUsers = salaryPreviewUsers.length > 0;
  const hasErrors = salaryPreviewErrors.length > 0;

  if (!isLoadingSalaryPreview && !hasUsers && !hasErrors) {
    return (
      <CommonTable
        columns={[]}
        data={[]}
        loading={isLoadingSalaryPreview}
        emptyMessage="No salary preview data available"
      />
    );
  }

  return (
    <Tabs
      value={salarySubTab}
      onValueChange={(value) => setSalarySubTab(value as SalarySubTab)}
      className="w-full space-y-4"
    >
      <TabsList className="bg-slate-100 p-1 rounded-xl flex w-fit gap-1 h-auto">
        {hasUsers && (
          <SalarySubTabTrigger
            value="users"
            label="Ready Users"
            count={salaryPreviewUsers.length}
            tone="emerald"
            icon={<CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />}
          />
        )}
        {hasErrors && (
          <SalarySubTabTrigger
            value="errors"
            label="Configuration Errors"
            count={salaryPreviewErrors.length}
            tone="rose"
            icon={<AlertCircle className="w-3.5 h-3.5 text-rose-600" />}
          />
        )}
      </TabsList>

      {hasUsers && (
        <TabsContent value="users" className="m-0 space-y-4 outline-none">
          <CommonTable
            columns={validUsersColumns}
            data={paginatedSalaryData as ISalaryPreviewUser[]}
            loading={isLoadingSalaryPreview}
            emptyMessage="No ready salary preview data available"
          />
          <TablePagination
            paginationData={salaryPreviewPaginationData}
            setPaginationData={setSalaryPreviewPaginationData}
            handleChangePageSize={onSalaryPreviewChangePageSize}
            isLoading={isLoadingSalaryPreview}
          />
        </TabsContent>
      )}

      {hasErrors && (
        <TabsContent value="errors" className="m-0 space-y-4 outline-none">
          <CommonTable
            columns={errorUsersColumns}
            data={paginatedSalaryData as ISalaryPreviewError[]}
            loading={isLoadingSalaryPreview}
            emptyMessage="No salary configuration errors"
          />
          <TablePagination
            paginationData={salaryPreviewPaginationData}
            setPaginationData={setSalaryPreviewPaginationData}
            handleChangePageSize={onSalaryPreviewChangePageSize}
            isLoading={isLoadingSalaryPreview}
          />
        </TabsContent>
      )}
    </Tabs>
  );
};

const SalarySubTabTrigger = ({
  value,
  label,
  count,
  tone,
  icon,
}: {
  value: SalarySubTab;
  label: string;
  count: number;
  tone: "emerald" | "rose";
  icon: React.ReactNode;
}) => {
  const badgeClass =
    tone === "emerald"
      ? "bg-emerald-100 text-emerald-700"
      : "bg-rose-100 text-rose-700";

  return (
    <TabsTrigger
      value={value}
      className="flex items-center gap-2 px-4 py-2 font-semibold text-xs rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-xs transition-all"
    >
      {icon}
      {label}
      <span className={`ml-1 px-2 py-0.5 text-[10px] font-bold rounded-full ${badgeClass}`}>
        {count}
      </span>
    </TabsTrigger>
  );
};

export default CreateSalaryTabs;
